import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfirmDialogComponent } from '@shared/components';
import { RefreshType, StaticMessage } from '@shared/enums';
import { Subproject } from '@shared/models';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { Constants, getStaticMessage, isEmptyObject } from '@shared/utils';
import {
  combineLatest,
  filter,
  mergeMap,
  of,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DataRefreshService, SignalRService, SubprojectService } from '.';
import { PersistenceService } from './persistence';

@UntilDestroy()
@Injectable()
export class NotificationService {
  private activeDialogCount = 0;
  private selectedSubprojectId: number;
  private activeSubprojects: Subproject[];

  constructor(
    private signalRService: SignalRService,
    private dataRefreshService: DataRefreshService,
    private persistenceService: PersistenceService,
    private subprojectService: SubprojectService,
    private userDetailsFacade: UserDetailsFacade,
    private appFacade: AppFacade,
    private router: Router,
    private dialog: MatDialog,
    private zone: NgZone
  ) {}

  setup(): void {
    this.signalRService.projectSetupChanged$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(
          this.userDetailsFacade.query.accountId$,
          this.userDetailsFacade.query.selectedSubprojectId$,
          this.appFacade.query.isAppOnline$
        ),
        // the current user made the changes, so nothing needs to be refreshed
        filter(
          ([data, accountId, selectedSubprojectId, isAppOnline]) =>
            accountId !== data.ChangedByAccountId &&
            isAppOnline &&
            selectedSubprojectId === data.SubprojectId
        ),
        tap(([data]) => {
          this.reloadProjectSetup(data.SubprojectId);
        })
      )
      .subscribe();

    this.signalRService.userRightsChanged$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(
          this.userDetailsFacade.query.accountId$,
          this.appFacade.query.isAppOnline$
        ),
        tap(([data, accountId, isAppOnline]) => {
          if (accountId === data.ChangedByAccountId) {
            // the current user made the changes, so nothing needs to be refreshed
            return;
          }

          if (isAppOnline && accountId === data.AccountId) {
            if (data.IsDeleted) {
              this.goToNoAccountErrorRoute();
              return;
            }

            this.reloadUserSession(
              $localize`:@@shared.notificationService.titleUserRightsChanged:User rights changed.`,
              $localize`:@@shared.notificationService.txtUserRightsChanged:An admin user has updated your user rights. The page will be reloaded.`
            );
          }
        })
      )
      .subscribe();

    this.signalRService.projectSettingsChanged$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(
          this.userDetailsFacade.query.accountId$,
          this.userDetailsFacade.query.selectedClientId$,
          this.userDetailsFacade.query.selectedSubproject$,
          this.appFacade.query.isAppOnline$
        ),
        tap(
          ([
            data,
            accountId,
            selectedClientId,
            selectedSubproject,
            isAppOnline,
          ]) => {
            const changesRelevantForCurrentClient =
              selectedClientId === data.ClientId ||
              selectedSubproject?.project?.assignedClientId === data.ClientId;

            if (
              accountId === data.ChangedByAccountId ||
              !changesRelevantForCurrentClient
            ) {
              // the current user made the changes or the changes are not relevant for the current client, so nothing needs to be refreshed
              // data.ClientId = null when the notification is broadcast to all clients
              return;
            }

            if (isAppOnline) {
              this.reloadUserSession(
                $localize`:@@shared.notificationService.titleProjectSettingsChanged:Project settings changed.`,
                $localize`:@@shared.notificationService.txtProjectSettingsChanged:An admin user has updated the project settings. The page will be reloaded.`
              );
            }
          }
        )
      )
      .subscribe();
  }

  private goToNoAccountErrorRoute(): void {
    this.router.navigate(['/error'], {
      queryParams: {
        errorType: 'no-account',
      },
    });
  }

  private reloadProjectSetup(subprojectId: number): void {
    this.zone.run(() => {
      if (this.pageReloadNeededForProjectSetup()) {
        this.activeDialogCount++;

        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: $localize`:@@shared.notificationService.titleProjectSetupChanged:Project setup changed.`,
            message: $localize`:@@shared.notificationService.txtProjectSetupChanged:An admin user has updated the project setup. The page will be reloaded.`,
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        confirmDialog
          .afterClosed()
          .pipe(
            untilDestroyed(this),
            mergeMap(() => {
              return this.persistenceService.reloadProjectSetup(
                subprojectId,
                true
              );
            }),
            tap(() => {
              this.activeDialogCount--;
              if (this.activeDialogCount === 0) {
                this.dataRefreshService.setRefreshNeeded(
                  RefreshType.ProjectSetupChange
                );
              }
            })
          )
          .subscribe();
      } else {
        this.persistenceService
          .reloadProjectSetup(subprojectId, true)
          .pipe(untilDestroyed(this))
          .subscribe();
      }
    });
  }

  private pageReloadNeededForProjectSetup(): boolean {
    return (
      this.router.url.includes('dpr-details') ||
      this.router.url.includes('dprs') ||
      this.router.url.includes('project-setup')
    );
  }

  private reloadUserSession(modalTitle: string, modalMessage: string): void {
    this.zone.run(() => {
      this.activeDialogCount++;

      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: modalTitle,
          message: modalMessage,
          buttonLabel: getStaticMessage(StaticMessage.OkButton),
          hideCancelButton: true,
        },
      });

      confirmDialog
        .afterClosed()
        .pipe(
          untilDestroyed(this),
          withLatestFrom(this.userDetailsFacade.query.selectedSubprojectId$),
          take(1),
          mergeMap(([_, selectedSubprojectId]) => {
            this.selectedSubprojectId = selectedSubprojectId;

            return this.persistenceService.reloadUserSession(true);
          }),
          mergeMap((userSession) => {
            this.activeSubprojects = userSession?.subprojects;
            return this.userDetailsFacade.query.userDetails$;
          }),
          take(1),
          mergeMap((userDetails) => {
            const selectedSubproject = userDetails?.selectedSubproject;

            if (
              selectedSubproject &&
              (!selectedSubproject.data ||
                isEmptyObject(selectedSubproject.data))
            ) {
              return combineLatest([
                this.persistenceService.reloadProjectSetup(
                  selectedSubproject.id,
                  true
                ),
                of(userDetails),
              ]);
            }
            return combineLatest([of(null), of(userDetails)]);
          }),
          tap(([_, userDetails]) => {
            if (
              userDetails?.selectedSubprojectId &&
              userDetails?.selectedSubprojectId !== this.selectedSubprojectId
            ) {
              this.subprojectService.redirectToSubprojectRoute(
                userDetails.selectedSubprojectId,
                userDetails
              );
            }

            this.activeDialogCount--;
            if (this.activeDialogCount === 0) {
              const isSubprojectStillActive = this.activeSubprojects?.some(
                (s) => s.id === this.selectedSubprojectId
              );
              this.dataRefreshService.setRefreshNeeded(
                RefreshType.UserSessionChange,
                { isSubprojectStillActive }
              );
            }
          })
        )
        .subscribe();
    });
  }
}
